import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";
import { ANSWERS } from "@/data/answers";

const SITE = "https://recoverytrauma.com";

export const Route = createFileRoute("/answers/$slug")({
  // Answer text is fetched when an answer page opens, not site-wide.
  loader: async ({ params }) => {
    const { answerBySlug } = await import("@/data/answers");
    const answer = answerBySlug(params.slug);
    if (!answer) throw notFound();
    return { answer };
  },

  head: ({ params, loaderData }) => {
    const url = `${SITE}/answers/${params.slug}`;
    if (!loaderData) {
      return {
        meta: [
          { title: "Unavailable — Recovery Trauma™" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { answer } = loaderData;
    return {
      meta: [
        { title: answer.title },
        { name: "description", content: answer.description },
        { property: "og:title", content: `${answer.question} — Recovery Trauma™` },
        { property: "og:description", content: answer.shortAnswer },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: answer.question,
            description: answer.description,
            mainEntityOfPage: url,
            inLanguage: "en",
            isAccessibleForFree: true,
            publisher: {
              "@type": "Organization",
              name: "Recovery Trauma\u2122",
              url: SITE,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Questions answered", item: `${SITE}/answers` },
              { "@type": "ListItem", position: 3, name: answer.question, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: AnswerNotFound,
  component: AnswerPage,
});

function AnswerNotFound() {
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-12 text-center">
      <h1 className="font-display text-2xl text-foreground">
        We couldn&rsquo;t find that question
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        It may have moved. All of the answers are listed together.
      </p>
      <Link
        to="/answers"
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full border border-[var(--gold-deep)]/40 bg-[var(--gradient-gold)] px-4 py-2 text-sm font-medium text-foreground"
      >
        See all questions <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function AnswerPage() {
  const { answer } = Route.useLoaderData();
  const others = ANSWERS.filter((a) => a.slug !== answer.slug).slice(0, 3);

  return (
    <article className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
      <Link
        to="/answers"
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Questions answered
      </Link>

      <h1 className="mt-3 font-display text-2xl leading-tight text-foreground sm:text-3xl">
        {answer.question}
      </h1>

      <p className="mt-4 rounded-3xl border border-gold/30 bg-[var(--gradient-gold)] px-4 py-4 text-sm leading-relaxed text-foreground sm:text-base">
        {answer.shortAnswer}
      </p>

      <div className="mt-8 space-y-8">
        {answer.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-display text-lg leading-snug text-foreground sm:text-xl">
              {section.heading}
            </h2>
            {section.paragraphs?.map((p) => (
              <p
                key={p}
                className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-[15px]"
              >
                {p}
              </p>
            ))}
            {section.bullets ? (
              <ul className="mt-3 space-y-2">
                {section.bullets.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground sm:text-[15px]"
                  >
                    <span
                      aria-hidden
                      className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--gold-deep)]"
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>

      <section className="mt-9">
        <h2 className="font-display text-lg text-foreground">Try it here</h2>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {answer.tools.map((tool) => (
            <Link
              key={tool.to + tool.label}
              to={tool.to}
              className="inline-flex min-h-11 flex-1 items-center justify-between gap-2 rounded-full border border-[var(--gold-deep)]/40 bg-[var(--gradient-gold)] px-4 py-2.5 text-[13px] font-medium text-foreground transition-all hover:shadow-md sm:text-sm"
            >
              {tool.label}
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg text-foreground">Read more</h2>
        <ul className="mt-3 space-y-2">
          {answer.related.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className="flex items-center gap-2.5 rounded-2xl border border-border/70 bg-card px-3.5 py-3 text-[13px] leading-snug text-foreground transition-colors hover:border-gold/50 sm:text-sm"
              >
                <BookOpen className="h-4 w-4 shrink-0 text-[var(--gold-deep)]" />
                <span>{link.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg text-foreground">
          Other questions people ask
        </h2>
        <ul className="mt-3 space-y-2">
          {others.map((o) => (
            <li key={o.slug}>
              <Link
                to="/answers/$slug"
                params={{ slug: o.slug }}
                className="flex items-center justify-between gap-2.5 rounded-2xl border border-border/70 bg-card px-3.5 py-3 text-[13px] leading-snug text-foreground transition-colors hover:border-gold/50 sm:text-sm"
              >
                <span>{o.question}</span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <p className="mt-8 rounded-2xl border border-border/60 bg-background/60 px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
        Recovery Trauma&trade; is an educational wellbeing platform. It is not
        therapy, medical treatment, diagnosis or emergency support, and this
        page cannot tell you what is happening for you. If you are in danger or
        in crisis, please use the{" "}
        <Link to="/crisis" className="underline underline-offset-2">
          Crisis Support page
        </Link>{" "}
        or your local emergency services.
      </p>
    </article>
  );
}
